import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import * as S from 'src/modules/programs/stepComponents/styles';
import { color, font } from 'src/shared/styles/variables';

import {
    changeFlexibilityExerciseAnswer,
    submitFlexibilityExerciseAnswers,
} from '../actions/weekFourActions';
import { QuestionHolder } from '../components/QuestionHolder';
import { Answer, OptionsQuestion } from '../models/weekFour';
import {
    getFlexibilityExerciseData,
    getFlexibilityExerciseSelectedAnswers,
    isFlexibilityExerciseDataValid,
} from '../selectors/weekFourSelectors';

const QuestionsHeader = styled('h4')(
    {
        color: color.chathamsBlue,
    },
    font.heavy(font.size.mediumExtended),
);

interface StateProps {
    data: OptionsQuestion[];
    isDataValid: boolean;
    selectedAnswers: Answer[];
}

interface ActionsProps {
    changeFlexibilityExerciseAnswer: typeof changeFlexibilityExerciseAnswer;
    submitFlexibilityExerciseAnswers: typeof submitFlexibilityExerciseAnswers;
}

type Props = StepButtonsProps & StateProps & ActionsProps;

class FlexibilityExerciseStepContainer extends React.Component<Props> {
    private handleAnswerChange = (value: string, field: string, id: number) =>
        this.props.changeFlexibilityExerciseAnswer({ value, field, id });

    private handleAdditionalInfoChange = (id: number) => (e: React.ChangeEvent<HTMLInputElement>) =>
        this.handleAnswerChange(e.target.value, 'additionalAnswer', id);

    private handleSubmit = () => {
        const { isDataValid } = this.props;

        if (!isDataValid) {
            return;
        }

        this.props.submitFlexibilityExerciseAnswers(this.props.selectedAnswers);
        this.props.goToNextStep();
    };

    public render(): JSX.Element {
        const { data, isDataValid } = this.props;

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading small>Developing Flexibility with Thoughts</S.StepHeading>
                        <S.StepHeading>Exercise</S.StepHeading>
                        <S.StepDescription small>
                            Go on, practice it with a pen or something else around you. In your
                            head, try saying “I can’t pick up this pen / paper / whatever” as you
                            pick it up! What was that like? Also try saying "I'm having the thought
                            that I can't pick up this pen" and see if that changes how much you feel
                            like you can pick up the pen!
                        </S.StepDescription>
                        <S.StepBoxWrapper>
                            <QuestionsHeader>What was that like?</QuestionsHeader>
                            {!isEmpty(data) &&
                                data.map(question => (
                                    <QuestionHolder
                                        key={question.id}
                                        question={question}
                                        handleAnswerChange={this.handleAnswerChange}
                                        handleInputChange={this.handleAdditionalInfoChange}
                                    />
                                ))}
                        </S.StepBoxWrapper>
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={this.props.goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!isDataValid}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    selectedAnswers: getFlexibilityExerciseSelectedAnswers(state),
    data: getFlexibilityExerciseData(state),
    isDataValid: isFlexibilityExerciseDataValid(state),
});

export const FlexibilityExerciseStep = connect(mapStateToProps, {
    changeFlexibilityExerciseAnswer,
    submitFlexibilityExerciseAnswers,
})(FlexibilityExerciseStepContainer);
